import React, { type CSSProperties, type ReactElement, type ReactNode } from "react";
import { createFormControl, useForm as useHookForm, type UseFormReturn } from "react-hook-form";
import { QuestionCircleOutlined } from "@ant-design/icons";
import { Col, Row, type ColProps } from "./Grid";
import { Tooltip } from "./Overlay";
import { cx, FormItemStatusContext, KitDisabledContext, KitSizeContext } from "./fieldContext";
import { runRules, type Rule, type RuleObject } from "./formRules";
import { useUiLanguage } from "./uiText";
import { useDialogAction } from "./dialogActionScope";
const CHANGE_TRIGGER = "onChange";

/*
 * Formularfassade des Kits auf react-hook-form (Plan 2026-10-13-antd-ausbau, Stufe 7, Entscheidung E2). Schnittstelle
 * und Verhalten wie Antd im genutzten Umfang: `Form`, `Form.Item`, `Form.List`, `Form.ErrorList`, `Form.useForm`,
 * `Form.useWatch`, `Form.useFormInstance` und die Methoden der Formularinstanz. react-hook-form hält die Werte und
 * benachrichtigt Beobachter; Registrierung, Regeln, Fehler und „berührt“ führt die Fassade wie Antd:
 * - `onFinish`/`validateFields`/`getFieldsValue()` liefern nur Werte gebundener Felder (und Listen),
 *   `getFieldsValue(true)` alle Werte.
 * - `setFieldsValue` führt tief zusammen, löst weder `onValuesChange` noch eine Prüfung aus.
 * - Nutzereingaben setzen „berührt“, rufen `onValuesChange` und prüfen nach `validateTrigger` (Standard `onChange`).
 * - Werte entfernter Felder bleiben erhalten (`preserve`).
 */

/* Wie Antd sind Formularwerte untypisiert bzw. über den Generikparameter typisiert. */
/* eslint-disable @typescript-eslint/no-explicit-any */
export type NamePath = string | number | ReadonlyArray<string | number>;
type Path = Array<string | number>;
type Store = Record<string, any>;

const toPath = (name: NamePath | undefined): Path => (name === undefined ? [] : Array.isArray(name) ? [...(name as Array<string | number>)] : [name as string | number]);
const keyOf = (path: Path) => path.map(String).join(".");
const sameKey = (a: Path, b: Path) => keyOf(a) === keyOf(b);

function getIn(source: unknown, path: Path): any {
  let current: any = source;
  for (const segment of path) {
    if (current == null) return undefined;
    current = current[segment as keyof typeof current];
  }
  return current;
}

function setIn(target: Store, path: Path, value: unknown): Store {
  if (!path.length) return value as Store;
  const [head, ...rest] = path;
  const base: any = Array.isArray(target) ? [...target] : { ...(target ?? {}) };
  const child = base[head as keyof typeof base];
  base[head as keyof typeof base] = rest.length ? setIn(child ?? (typeof rest[0] === "number" ? [] : {}), rest, value) : value;
  return base;
}

/** Setzt einen Wert ohne Benachrichtigung direkt im Speicher (nur vor dem ersten Rendern der Felder). */
function writeIn(target: Store, path: Path, value: unknown) {
  let current: any = target;
  path.forEach((segment, index) => {
    if (index === path.length - 1) { current[segment] = value; return; }
    if (current[segment] == null || typeof current[segment] !== "object") current[segment] = typeof path[index + 1] === "number" ? [] : {};
    current = current[segment];
  });
}

const isPlainObject = (value: unknown): value is Store => typeof value === "object" && value !== null && !Array.isArray(value) && Object.getPrototypeOf(value) === Object.prototype;

/** Tiefes Zusammenführen wie Antd `setFieldsValue`: Objekte rekursiv, Arrays und andere Werte ersetzen. */
function deepMerge(target: unknown, source: unknown): any {
  if (!isPlainObject(source) || !isPlainObject(target)) return source;
  const result: Store = { ...target };
  for (const [key, value] of Object.entries(source)) result[key] = isPlainObject(value) && isPlainObject(result[key]) ? deepMerge(result[key], value) : value;
  return result;
}

export type FieldError = { name: Path; errors: ReactNode[]; warnings: ReactNode[] };
export type ValidateErrorEntity<V = any> = { values: V; errorFields: Array<{ name: Path; errors: string[]; warnings: string[] }>; outOfDate: boolean };
export type FieldData = { name: NamePath; value?: any; errors?: ReactNode[]; warnings?: ReactNode[]; touched?: boolean; validating?: boolean };

type FieldEntry = {
  path: Path;
  rules: () => RuleObject[];
  label: () => string;
  initialValue?: unknown;
  validateTrigger: string[];
  dependencies: Path[];
  isList?: boolean;
};

export interface FormInstance<Values = any> {
  getFieldValue: (name: NamePath) => any;
  getFieldsValue: ((nameList?: NamePath[] | true, filter?: (meta: { touched: boolean; validating: boolean; name: Path }) => boolean) => any) & (() => Values);
  setFieldValue: (name: NamePath, value: any) => void;
  setFieldsValue: (values: any) => void;
  setFields: (fields: FieldData[]) => void;
  resetFields: (fields?: NamePath[]) => void;
  validateFields: (nameList?: NamePath[] | { validateOnly?: boolean; recursive?: boolean }, options?: { validateOnly?: boolean; recursive?: boolean }) => Promise<Values>;
  submit: () => void;
  getFieldError: (name: NamePath) => string[];
  getFieldsError: (nameList?: NamePath[]) => Array<{ name: Path; errors: string[]; warnings: string[] }>;
  getFieldWarning: (name: NamePath) => string[];
  isFieldTouched: (name: NamePath) => boolean;
  isFieldsTouched: (nameList?: NamePath[] | boolean, allTouched?: boolean) => boolean;
  isFieldValidating: (name: NamePath) => boolean;
  scrollToField: (name: NamePath, options?: ScrollIntoViewOptions) => void;
  focusField: (name: NamePath) => void;
  getFieldInstance: (name: NamePath) => HTMLElement | null;
}

type FormCallbacks = {
  onFinish?: (values: any) => void;
  onFinishFailed?: (info: ValidateErrorEntity) => void;
  onValuesChange?: (changed: any, all: any) => void;
  scrollToFirstError?: boolean | ScrollIntoViewOptions;
};

/** Zustand eines Formulars neben den Werten in react-hook-form. */
class FormStore {
  methods!: UseFormReturn<Store>;
  initialValues: Store = {};
  fields = new Map<string, FieldEntry>();
  errors = new Map<string, { errors: ReactNode[]; warnings: ReactNode[] }>();
  touched = new Set<string>();
  validating = new Set<string>();
  callbacks: FormCallbacks = {};
  /** Zählt jede Wertänderung (react-hook-form und stilles Vorbelegen); Beobachter gleichen damit verpasste Änderungen ab. */
  valuesVersion = 0;
  private attached: unknown = null;

  /** Verbindet den Speicher mit react-hook-form; jede Wertänderung erhöht `valuesVersion`. */
  attach(methods: UseFormReturn<Store>) {
    this.methods = methods;
    if (this.attached === methods.control) return;
    this.attached = methods.control;
    methods.control._subscribe({ formState: { values: true }, callback: () => { this.valuesVersion++; } });
  }
  formName?: string;
  lang = "de";
  private listeners = new Set<() => void>();
  private version = 0;
  instance: FormInstance;

  constructor() {
    this.instance = this.createInstance();
  }

  subscribe = (listener: () => void) => { this.listeners.add(listener); return () => { this.listeners.delete(listener); }; };
  getVersion = () => this.version;
  private notify() { this.version++; this.listeners.forEach((listener) => listener()); }

  fieldId(path: Path) {
    const joined = path.map(String).join("_");
    return this.formName ? `${this.formName}_${joined}` : joined;
  }

  register(entry: FieldEntry) {
    const key = keyOf(entry.path);
    // Kit controls are registered leaves, including atomic values such as Dayjs and date ranges.
    this.methods.register(key);
    this.fields.set(key, entry);
    if (entry.initialValue !== undefined && this.getValue(entry.path) === undefined) this.setValueSilently(entry.path, entry.initialValue);
    return () => { if (this.fields.get(key) === entry) this.fields.delete(key); };
  }

  getValue(path: Path) {
    return path.length ? this.methods.getValues(keyOf(path) as never) : this.methods.getValues();
  }

  setValueSilently(path: Path, value: unknown) {
    if (!path.length) return;
    // Imperative updates may target a currently unmounted atomic field as well.
    if (!isPlainObject(value) && !Array.isArray(value)) this.methods.register(keyOf(path));
    this.methods.setValue(keyOf(path) as never, value as never, { shouldDirty: true });
  }

  /** Werte der gebundenen Felder (Antd `getFieldsValue()` und `onFinish`). */
  registeredValues(): Store {
    let result: Store = {};
    const all = this.methods.getValues();
    for (const entry of this.fields.values()) {
      if (!entry.path.length) continue;
      result = setIn(result, entry.path, getIn(all, entry.path));
    }
    return result;
  }

  initialValueOf(path: Path) {
    const entry = this.fields.get(keyOf(path));
    const fromForm = getIn(this.initialValues, path);
    return fromForm !== undefined ? fromForm : entry?.initialValue;
  }

  setErrors(path: Path, result: { errors: ReactNode[]; warnings: ReactNode[] } | null) {
    const key = keyOf(path);
    const previous = this.errors.get(key);
    if (!result || (!result.errors.length && !result.warnings.length)) {
      if (!previous) return;
      this.errors.delete(key);
    } else {
      this.errors.set(key, result);
    }
    this.notify();
  }

  async validatePaths(paths: Path[]): Promise<FieldError[]> {
    const values = this.methods.getValues();
    const results = await Promise.all(paths.map(async (path) => {
      const entry = this.fields.get(keyOf(path));
      if (!entry) return { name: path, errors: [], warnings: [] };
      const rules = entry.rules();
      if (!rules.length) { this.setErrors(path, null); return { name: path, errors: [], warnings: [] }; }
      this.validating.add(keyOf(path));
      const result = await runRules(rules, getIn(values, path), entry.label(), this.lang);
      this.validating.delete(keyOf(path));
      this.setErrors(path, result);
      return { name: path, ...result };
    }));
    return results;
  }

  /** Nutzereingabe an einem Feld: Wert setzen, berührt, `onValuesChange`, Prüfung nach Auslöser und abhängige Felder. */
  userChange(path: Path, value: unknown, trigger: string) {
    this.setValueSilently(path, value);
    const key = keyOf(path);
    this.touched.add(key);
    this.callbacks.onValuesChange?.(setIn({}, path, value), this.registeredValues());
    const entry = this.fields.get(key);
    const toValidate: Path[] = [];
    if (entry && entry.validateTrigger.includes(trigger)) toValidate.push(path);
    for (const [otherKey, other] of this.fields) {
      if (otherKey === key || !other.dependencies.some((dependency) => sameKey(dependency, path))) continue;
      if (this.touched.has(otherKey) || this.errors.has(otherKey)) toValidate.push(other.path);
    }
    if (toValidate.length) void this.validatePaths(toValidate);
    this.notify();
  }

  userBlur(path: Path) {
    const entry = this.fields.get(keyOf(path));
    if (entry?.validateTrigger.includes("onBlur")) void this.validatePaths([path]);
  }

  async validate(nameList?: NamePath[], options?: { validateOnly?: boolean; recursive?: boolean }): Promise<Store> {
    const requested = nameList?.map(toPath);
    const paths = [...this.fields.values()]
      .map((entry) => entry.path)
      .filter((path) => !requested || requested.some((wanted) => sameKey(wanted, path) || (options?.recursive && keyOf(path).startsWith(`${keyOf(wanted)}.`))));
    const results = await this.validatePaths(paths);
    if (options?.validateOnly) results.forEach((result) => this.setErrors(result.name, null));
    const values = requested ? requested.reduce<Store>((acc, path) => setIn(acc, path, this.getValue(path)), {}) : this.registeredValues();
    const failed = results.filter((result) => result.errors.length);
    if (failed.length) {
      const error: ValidateErrorEntity = {
        values,
        errorFields: failed.map((result) => ({ name: result.name, errors: result.errors.map(String), warnings: result.warnings.map(String) })),
        outOfDate: false,
      };
      throw error;
    }
    return values;
  }

  submit() {
    this.validate().then(
      (values) => this.callbacks.onFinish?.(values),
      (error: ValidateErrorEntity) => {
        if (!error || !Array.isArray(error.errorFields)) { console.error(error); return; }
        const scroll = this.callbacks.scrollToFirstError;
        if (scroll && error.errorFields[0]) this.instance.scrollToField(error.errorFields[0].name, typeof scroll === "object" ? scroll : { block: "nearest" });
        this.callbacks.onFinishFailed?.(error);
      },
    );
  }

  private createInstance(): FormInstance {
    const store = this;
    return {
      getFieldValue: (name) => store.getValue(toPath(name)),
      getFieldsValue: ((nameList?: NamePath[] | true, filter?: (meta: { touched: boolean; validating: boolean; name: Path }) => boolean) => {
        if (nameList === true) return store.methods.getValues();
        if (Array.isArray(nameList)) return nameList.map(toPath).reduce<Store>((acc, path) => setIn(acc, path, store.getValue(path)), {});
        if (filter) {
          let result: Store = {};
          for (const entry of store.fields.values()) {
            const key = keyOf(entry.path);
            if (filter({ touched: store.touched.has(key), validating: store.validating.has(key), name: entry.path })) result = setIn(result, entry.path, store.getValue(entry.path));
          }
          return result;
        }
        return store.registeredValues();
      }) as FormInstance["getFieldsValue"],
      setFieldValue: (name, value) => { store.setValueSilently(toPath(name), value); store.notify(); },
      setFieldsValue: (values) => {
        if (!isPlainObject(values)) return;
        const current = store.methods.getValues();
        for (const [key, value] of Object.entries(values)) {
          const merged = isPlainObject(value) && isPlainObject(current[key]) ? deepMerge(current[key], value) : value;
          store.setValueSilently([key], merged);
        }
        store.notify();
      },
      setFields: (fields) => {
        for (const field of fields) {
          const path = toPath(field.name);
          if ("value" in field) store.setValueSilently(path, field.value);
          if (field.touched !== undefined) { if (field.touched) store.touched.add(keyOf(path)); else store.touched.delete(keyOf(path)); }
          if (field.errors !== undefined || field.warnings !== undefined) store.setErrors(path, { errors: field.errors ?? [], warnings: field.warnings ?? [] });
        }
        store.notify();
      },
      resetFields: (names) => {
        if (!names) {
          let next: Store = structuredCloneSafe(store.initialValues);
          for (const entry of store.fields.values()) {
            if (entry.initialValue !== undefined && getIn(next, entry.path) === undefined) next = setIn(next, entry.path, entry.initialValue);
          }
          store.methods.reset(next, { keepDefaultValues: true });
          store.errors.clear();
          store.touched.clear();
        } else {
          for (const path of names.map(toPath)) {
            store.setValueSilently(path, store.initialValueOf(path));
            store.errors.delete(keyOf(path));
            store.touched.delete(keyOf(path));
          }
        }
        store.notify();
      },
      validateFields: (nameList, options) => {
        if (nameList && !Array.isArray(nameList)) return store.validate(undefined, nameList);
        return store.validate(nameList as NamePath[] | undefined, options);
      },
      submit: () => store.submit(),
      getFieldError: (name) => (store.errors.get(keyOf(toPath(name)))?.errors ?? []).map(String),
      getFieldWarning: (name) => (store.errors.get(keyOf(toPath(name)))?.warnings ?? []).map(String),
      getFieldsError: (nameList) => {
        const paths = nameList ? nameList.map(toPath) : [...store.fields.values()].map((entry) => entry.path);
        return paths.map((path) => { const entry = store.errors.get(keyOf(path)); return { name: path, errors: (entry?.errors ?? []).map(String), warnings: (entry?.warnings ?? []).map(String) }; });
      },
      isFieldTouched: (name) => store.touched.has(keyOf(toPath(name))),
      isFieldsTouched: (nameList, allTouched) => {
        const list = Array.isArray(nameList) ? nameList.map(toPath) : [...store.fields.values()].map((entry) => entry.path);
        const all = typeof nameList === "boolean" ? nameList : Boolean(allTouched);
        const touched = list.map((path) => store.touched.has(keyOf(path)) || [...store.touched].some((key) => key.startsWith(`${keyOf(path)}.`)));
        return all ? touched.every(Boolean) : touched.some(Boolean);
      },
      isFieldValidating: (name) => store.validating.has(keyOf(toPath(name))),
      scrollToField: (name, options) => document.getElementById(store.fieldId(toPath(name)))?.scrollIntoView(options ?? { block: "nearest" }),
      focusField: (name) => document.getElementById(store.fieldId(toPath(name)))?.focus(),
      getFieldInstance: (name) => document.getElementById(store.fieldId(toPath(name))),
    };
  }
}

function structuredCloneSafe<T>(value: T): T {
  if (!isPlainObject(value) && !Array.isArray(value)) return value;
  if (Array.isArray(value)) return value.map(structuredCloneSafe) as T;
  return Object.fromEntries(Object.entries(value as Store).map(([key, item]) => [key, structuredCloneSafe(item)])) as T;
}

/**
 * Abonniert Werte im Speicher von react-hook-form (`control._subscribe`, wie dessen `useWatch`) und liest beim Rendern
 * frisch: anders als `useWatch` stimmt der Wert auch nach einem Namenswechsel (umnummerierte Listenzeile). Objekte und
 * Arrays bekommen bei jeder Änderung eine neue Identität, damit `useMemo`/Effekte der Aufrufer reagieren.
 */
function useStoreWatch(store: FormStore, path: Path | null, disabled = false) {
  const control = store.methods.control;
  const key = path && path.length ? keyOf(path) : undefined;
  const [, rerender] = React.useReducer((count: number) => count + 1, 0);
  const seenVersion = React.useRef(store.valuesVersion);
  seenVersion.current = store.valuesVersion;
  React.useLayoutEffect(() => {
    if (disabled) return;
    const unsubscribe = control._subscribe({ name: key as never, formState: { values: true }, exact: false, callback: () => rerender() });
    // Änderungen zwischen Rendern und Abonnieren (z. B. Startwerte, die ein Kindformular setzt) nachholen.
    if (store.valuesVersion !== seenVersion.current) rerender();
    return unsubscribe;
  }, [control, key, disabled, store]);
  const raw = path && path.length ? getIn(control._formValues, path) : control._formValues;
  const cache = React.useRef<{ version: number; key: string | undefined; raw: unknown; out: unknown } | null>(null);
  if (cache.current && cache.current.version === store.valuesVersion && cache.current.key === key && cache.current.raw === raw) return cache.current.out;
  const out = isPlainObject(raw) || Array.isArray(raw) ? structuredCloneSafe(raw) : raw;
  cache.current = { version: store.valuesVersion, key, raw, out };
  return out;
}

const stores = new WeakMap<FormInstance, FormStore>();
/** Ersatzspeicher für Felder ohne umgebendes Formular (Antd warnt dort nur). */
const EMPTY_STORE = new FormStore();
EMPTY_STORE.attach(createFormControl<Store>({ shouldUnregister: false }) as unknown as UseFormReturn<Store>);
const storeOf = (instance: FormInstance | undefined) => (instance ? stores.get(instance) : undefined);

/** Formularinstanz wie Antd `Form.useForm()`; die Werte liegen in react-hook-form. */
function useForm<Values = any>(form?: FormInstance<Values>): [FormInstance<Values>] {
  const methods = useHookForm<Store>({ shouldUnregister: false });
  const ref = React.useRef<FormStore | null>(null);
  if (!ref.current) {
    const existing = storeOf(form as FormInstance | undefined);
    ref.current = existing ?? new FormStore();
    if (!existing) stores.set(ref.current.instance, ref.current);
  }
  if (!ref.current.methods) ref.current.attach(methods);
  return [ref.current.instance as FormInstance<Values>];
}

// ---------------------------------------------------------------- Kontexte

type LabelColProps = ColProps;
type FormContextValue = {
  store: FormStore;
  layout: "horizontal" | "vertical" | "inline";
  labelCol?: LabelColProps;
  wrapperCol?: LabelColProps;
  labelAlign?: "left" | "right";
  labelWrap?: boolean;
  colon: boolean;
  requiredMark: boolean | "optional" | ((label: ReactNode, info: { required: boolean }) => ReactNode);
  validateTrigger: string[];
};
const FormContext = React.createContext<FormContextValue | null>(null);
const ListPrefixContext = React.createContext<Path>([]);

function useFormStore(): FormStore | undefined {
  return React.useContext(FormContext)?.store;
}

function useStoreVersion(store: FormStore | undefined) {
  return React.useSyncExternalStore(
    React.useCallback((listener: () => void) => (store ? store.subscribe(listener) : () => undefined), [store]),
    () => (store ? store.getVersion() : 0),
    () => 0,
  );
}

// ---------------------------------------------------------------- Form

export interface FormProps<Values = any> extends Omit<React.FormHTMLAttributes<HTMLFormElement>, "onSubmit" | "children"> {
  form?: FormInstance<Values>;
  name?: string;
  layout?: "horizontal" | "vertical" | "inline";
  initialValues?: Partial<Values> | Store;
  onFinish?: (values: Values) => void;
  onFinishFailed?: (info: ValidateErrorEntity<Values>) => void;
  onValuesChange?: (changedValues: any, allValues: Values) => void;
  requiredMark?: boolean | "optional" | ((label: ReactNode, info: { required: boolean }) => ReactNode);
  disabled?: boolean;
  size?: "small" | "middle" | "large";
  colon?: boolean;
  labelCol?: LabelColProps;
  wrapperCol?: LabelColProps;
  labelAlign?: "left" | "right";
  labelWrap?: boolean;
  validateTrigger?: string | string[];
  scrollToFirstError?: boolean | ScrollIntoViewOptions;
  /** `false`: kein eigenes `<form>`-Element (eingebettet in ein äußeres Formular). */
  component?: false | string;
  preserve?: boolean;
  clearOnDestroy?: boolean;
  /** Antd-Optionen ohne Wirkung im Kit. */
  validateMessages?: unknown;
  variant?: string;
  feedbackIcons?: unknown;
  /** Wie Antd: die Ref zeigt auf die Formularinstanz. */
  ref?: React.Ref<FormInstance<Values>>;
  children?: ReactNode | ((values: Values, form: FormInstance<Values>) => ReactNode);
}

function FormBase<Values = any>(props: FormProps<Values>) {
  const {
    form, name, layout = "horizontal", initialValues, onFinish, onFinishFailed, onValuesChange, requiredMark = true, disabled, size, colon = true,
    labelCol, wrapperCol, labelAlign, labelWrap, validateTrigger = "onChange", scrollToFirstError, component, clearOnDestroy, children, className,
    preserve: _preserve, validateMessages: _messages, variant: _variant, feedbackIcons: _icons, ref, ...rest
  } = props;
  const [instance] = useForm<Values>(form);
  React.useImperativeHandle(ref, () => instance, [instance]);
  const store = storeOf(instance as FormInstance)!;
  const lang = useUiLanguage();
  const action = useDialogAction();
  store.callbacks = {
    onFinish: values => {
      // Der Scope besitzt das Promise samt Fehlerbehandlung. Nicht an die interne
      // Validierungs-then-Kette zurückgeben, die nur den nativen Submit auslöst.
      if (action) action.invoke(() => onFinish?.(values));
      else onFinish?.(values);
    },
    onFinishFailed: onFinishFailed as FormCallbacks["onFinishFailed"],
    onValuesChange: onValuesChange as FormCallbacks["onValuesChange"], scrollToFirstError,
  };
  store.formName = name;
  store.lang = lang;

  // Startwerte wie Antd: beim ersten Rendern, bevor die Felder lesen; bereits gesetzte Werte haben Vorrang,
  // `Form.Item initialValue` greift nur, wo das Formular keinen Startwert hat.
  const appliedRef = React.useRef(false);
  if (!appliedRef.current) {
    appliedRef.current = true;
    store.initialValues = structuredCloneSafe((initialValues ?? {}) as Store);
    if (initialValues && Object.keys(initialValues).length) {
      const control = store.methods.control;
      const merged = deepMerge(structuredCloneSafe(initialValues as Store), control._formValues);
      Object.assign(control._formValues, merged);
      store.valuesVersion++;
      Object.assign(control._defaultValues, deepMerge(structuredCloneSafe(initialValues as Store), control._defaultValues));
    }
  }
  // Beobachter außerhalb des Formulars (z. B. `useWatch` im Elternteil) erfahren die Startwerte.
  React.useLayoutEffect(() => {
    if (!initialValues) return;
    for (const key of Object.keys(initialValues)) store.methods.setValue(key as never, store.methods.getValues(key) as never, { shouldDirty: false });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  React.useEffect(() => () => { if (clearOnDestroy) instance.resetFields(); }, [clearOnDestroy, instance]);

  const context: FormContextValue = {
    store, layout, labelCol, wrapperCol, labelAlign, labelWrap, colon, requiredMark,
    validateTrigger: Array.isArray(validateTrigger) ? validateTrigger : [validateTrigger],
  };
  const values = useStoreWatch(store, null, typeof children !== "function") as Values;
  const content = typeof children === "function" ? children(values, instance) : children;
  const classes = cx("ck-form", `ck-form-${layout}`, requiredMark === false && "ck-form-hide-required-mark", size === "small" && "ck-form-small", size === "large" && "ck-form-large", className);
  let body: ReactNode;
  if (component === false) body = content;
  else {
    const Element = (component ?? "form") as "form";
    body = (
      <Element
        {...rest}
        id={rest.id ?? name}
        className={classes}
        noValidate
        onSubmit={(event: React.FormEvent<HTMLFormElement>) => { event.preventDefault(); event.stopPropagation(); store.submit(); }}
        onReset={(event: React.FormEvent<HTMLFormElement>) => { event.preventDefault(); instance.resetFields(); }}
      >
        {content}
      </Element>
    );
  }
  const inherited = React.useContext(KitDisabledContext);
  const inheritedSize = React.useContext(KitSizeContext);
  return (
    <FormContext.Provider value={context}>
      <ListPrefixContext.Provider value={[]}>
        <KitDisabledContext.Provider value={disabled ?? inherited}>
          <KitSizeContext.Provider value={size ?? inheritedSize}>{body}</KitSizeContext.Provider>
        </KitDisabledContext.Provider>
      </ListPrefixContext.Provider>
    </FormContext.Provider>
  );
}

// ---------------------------------------------------------------- Form.Item

export interface FormItemProps<Values = any> {
  name?: NamePath;
  label?: ReactNode;
  rules?: Rule[];
  children?: ReactNode | ((form: FormInstance<Values>) => ReactNode);
  valuePropName?: string;
  trigger?: string;
  validateTrigger?: string | string[] | false;
  getValueFromEvent?: (...args: any[]) => any;
  getValueProps?: (value: any) => Record<string, unknown>;
  normalize?: (value: any, prevValue: any, allValues: any) => any;
  dependencies?: NamePath[];
  shouldUpdate?: boolean | ((prevValues: any, curValues: any) => boolean);
  initialValue?: any;
  noStyle?: boolean;
  hidden?: boolean;
  required?: boolean;
  tooltip?: ReactNode | { title?: ReactNode; icon?: ReactNode };
  extra?: ReactNode;
  help?: ReactNode;
  validateStatus?: "success" | "warning" | "error" | "validating" | "";
  hasFeedback?: boolean;
  colon?: boolean;
  labelCol?: LabelColProps;
  wrapperCol?: LabelColProps;
  labelAlign?: "left" | "right";
  htmlFor?: string;
  preserve?: boolean;
  messageVariables?: Record<string, string>;
  layout?: "horizontal" | "vertical";
  style?: CSSProperties;
  className?: string;
  id?: string;
}

function labelText(label: ReactNode, path: Path): string {
  if (typeof label === "string" || typeof label === "number") return String(label);
  return path.length ? String(path[path.length - 1]) : "";
}

const defaultValueFromEvent = (valuePropName: string, ...args: any[]) => {
  const event = args[0];
  if (event && typeof event === "object" && "target" in event && event.target && typeof event.target === "object" && valuePropName in event.target) return event.target[valuePropName];
  return event;
};

function FormItem<Values = any>(props: FormItemProps<Values>) {
  const context = React.useContext(FormContext);
  const prefix = React.useContext(ListPrefixContext);
  const store = context?.store;
  const {
    name, label, rules, children, valuePropName = "value", trigger = "onChange", validateTrigger, getValueFromEvent, getValueProps, normalize,
    dependencies, shouldUpdate, initialValue, noStyle, hidden, required, tooltip, extra, help, validateStatus, colon, labelCol, wrapperCol,
    labelAlign, htmlFor, messageVariables, layout: itemLayout, style, className, id: itemId,
  } = props;
  const path = name === undefined ? null : [...prefix, ...toPath(name)];
  const key = path ? keyOf(path) : "";
  const rulesRef = React.useRef(rules);
  rulesRef.current = rules;
  const labelRef = React.useRef(label);
  labelRef.current = label;
  const triggers = validateTrigger === false ? [] : validateTrigger === undefined ? context?.validateTrigger ?? ["onChange"] : Array.isArray(validateTrigger) ? validateTrigger : [validateTrigger];

  React.useLayoutEffect(() => {
    if (!store || !path) return;
    return store.register({
      path,
      rules: () => (rulesRef.current ?? []).map((rule) => (typeof rule === "function" ? rule(store.instance) : rule)),
      label: () => messageVariables?.label ?? labelText(labelRef.current, path),
      initialValue,
      validateTrigger: triggers,
      dependencies: (dependencies ?? []).map(toPath),
    });
  }, [store, key, triggers.join("|"), (dependencies ?? []).map((item) => keyOf(toPath(item))).join("|")]); // eslint-disable-line react-hooks/exhaustive-deps

  const control = (store ?? EMPTY_STORE).methods.control;
  // `initialValue` vor dem ersten Lesen setzen, damit das Feld nicht erst leer erscheint.
  const seededRef = React.useRef(false);
  if (!seededRef.current && store && path && initialValue !== undefined) {
    seededRef.current = true;
    if (getIn(control._formValues, path) === undefined) {
      writeIn(control._formValues, path, structuredCloneSafe(initialValue));
      store.valuesVersion++;
      if (getIn(control._defaultValues, path) === undefined) writeIn(control._defaultValues as Store, path, structuredCloneSafe(initialValue));
    }
  }
  const value = useStoreWatch(store ?? EMPTY_STORE, path, !path || !store);
  const watchAll = Boolean(shouldUpdate) || (typeof children === "function" && Boolean(dependencies?.length));
  useStoreWatch(store ?? EMPTY_STORE, null, !watchAll || !store);
  useStoreVersion(store);
  const fieldErrors = path && store ? store.errors.get(key) : undefined;
  const status = validateStatus ?? (fieldErrors?.errors.length ? "error" : fieldErrors?.warnings.length ? "warning" : "");
  const fieldId = itemId ?? (path && store ? store.fieldId(path) : undefined);
  const helpId = fieldId ? `${fieldId}_help` : undefined;

  let control_: ReactNode;
  if (typeof children === "function") {
    control_ = store ? children(store.instance as FormInstance<Values>) : null;
  } else if (path && store && React.isValidElement(children)) {
    const child = children as ReactElement<Record<string, any>>;
    const childProps = child.props;
    const valueProps = getValueProps ? getValueProps(value) : { [valuePropName]: value };
    const handler = (...args: any[]) => {
      const raw = getValueFromEvent ? getValueFromEvent(...args) : defaultValueFromEvent(valuePropName, ...args);
      const next = normalize ? normalize(raw, value, store.methods.getValues()) : raw;
      store.userChange(path, next, trigger === CHANGE_TRIGGER ? CHANGE_TRIGGER : trigger);
      if (trigger !== CHANGE_TRIGGER && triggers.includes(CHANGE_TRIGGER)) void store.validatePaths([path]);
      childProps[trigger]?.(...args);
    };
    const extraTriggers: Record<string, (...args: any[]) => void> = {};
    for (const event of triggers) {
      if (event === trigger || event === CHANGE_TRIGGER) continue;
      extraTriggers[event] = (...args: any[]) => { void store.validatePaths([path]); childProps[event]?.(...args); };
    }
    control_ = React.cloneElement(child, {
      ...valueProps,
      id: childProps.id ?? fieldId,
      "aria-invalid": status === "error" || undefined,
      "aria-describedby": fieldErrors?.errors.length || help ? helpId : childProps["aria-describedby"],
      ...extraTriggers,
      [trigger]: handler,
    });
  } else {
    control_ = children as ReactNode;
  }

  const statusValue = { status: status === "error" || status === "warning" ? status : "" as const };
  const wrapped = <FormItemStatusContext.Provider value={statusValue}>{control_}</FormItemStatusContext.Provider>;
  if (noStyle) return hidden ? <div style={{ display: "none" }}>{wrapped}</div> : wrapped;

  const layout = itemLayout ?? (context?.layout === "inline" ? "inline" : context?.layout ?? "vertical");
  const isRequired = required ?? (rules ?? []).some((rule) => typeof rule === "object" && rule.required);
  const markMode = context?.requiredMark ?? true;
  const showColon = (colon ?? context?.colon ?? true) && layout === "horizontal" && typeof label === "string";
  const tooltipConfig = tooltip == null ? null : typeof tooltip === "object" && !React.isValidElement(tooltip) && ("title" in (tooltip as object) || "icon" in (tooltip as object)) ? tooltip as { title?: ReactNode; icon?: ReactNode } : { title: tooltip as ReactNode };
  let labelContent: ReactNode = label;
  if (typeof markMode === "function") labelContent = markMode(label, { required: isRequired });
  const labelNode = label !== undefined && label !== null && label !== false ? (
    <Col {...(layout === "horizontal" ? labelCol ?? context?.labelCol : undefined)} className={cx("ck-form-item-label", (labelAlign ?? context?.labelAlign) === "left" && "ck-form-item-label-left", context?.labelWrap && "ck-form-item-label-wrap")}>
      <label
        htmlFor={htmlFor ?? fieldId}
        className={cx(isRequired && markMode === true && "ck-form-item-required", !showColon && "ck-form-item-no-colon")}
        title={typeof label === "string" ? label : undefined}
      >
        {labelContent}
        {tooltipConfig ? (
          <Tooltip title={tooltipConfig.title}>
            <span className="ck-form-item-tooltip" role="img" aria-label="?">{tooltipConfig.icon ?? <QuestionCircleOutlined />}</span>
          </Tooltip>
        ) : null}
        {markMode === "optional" && !isRequired ? <span className="ck-form-item-optional">(optional)</span> : null}
      </label>
    </Col>
  ) : null;
  const messages = help !== undefined ? (help === null || help === false ? [] : [help]) : [...(fieldErrors?.errors ?? []), ...(fieldErrors?.warnings ?? [])];
  return (
    <div className={cx("ck-form-item", status === "error" && "ck-form-item-has-error", status === "warning" && "ck-form-item-has-warning", hidden && "ck-form-item-hidden", `ck-form-item-${layout}`, className)} style={hidden ? { ...style, display: "none" } : style}>
      <Row className="ck-form-item-row" wrap={layout !== "horizontal" ? true : undefined}>
        {labelNode}
        <Col {...(layout === "horizontal" ? wrapperCol ?? context?.wrapperCol : undefined)} className="ck-form-item-control">
          <div className="ck-form-item-control-input">
            <div className="ck-form-item-control-input-content">{wrapped}</div>
          </div>
          {messages.length ? (
            <div id={helpId} className="ck-form-item-explain" role="alert">
              {messages.map((message, index) => (
                <div key={index} className={cx(fieldErrors?.errors.includes(message) || validateStatus === "error" ? "ck-form-item-explain-error" : fieldErrors?.warnings.includes(message) ? "ck-form-item-explain-warning" : "ck-form-item-explain-help")}>{message}</div>
              ))}
            </div>
          ) : null}
          {extra != null ? <div className="ck-form-item-extra">{extra}</div> : null}
        </Col>
      </Row>
    </div>
  );
}

// ---------------------------------------------------------------- Form.List

export type FormListFieldData = { key: number; name: number; isListField: boolean; fieldKey: number };
export type FormListOperation = { add: (defaultValue?: any, insertIndex?: number) => void; remove: (index: number | number[]) => void; move: (from: number, to: number) => void };

export interface FormListProps {
  name: NamePath;
  rules?: Rule[];
  initialValue?: any[];
  children: (fields: FormListFieldData[], operation: FormListOperation, meta: { errors: ReactNode[]; warnings: ReactNode[] }) => ReactNode;
}

let listKeySeed = 0;

function FormList({ name, rules, initialValue, children }: FormListProps) {
  const context = React.useContext(FormContext);
  const prefix = React.useContext(ListPrefixContext);
  const store = context?.store;
  const path = [...prefix, ...toPath(name)];
  const key = keyOf(path);
  const rulesRef = React.useRef(rules);
  rulesRef.current = rules;
  React.useLayoutEffect(() => {
    if (!store) return;
    return store.register({
      path,
      rules: () => (rulesRef.current ?? []).map((rule) => (typeof rule === "function" ? rule(store.instance) : rule)),
      label: () => String(path[path.length - 1] ?? ""),
      initialValue,
      validateTrigger: [],
      dependencies: [],
      isList: true,
    });
  }, [store, key]); // eslint-disable-line react-hooks/exhaustive-deps
  const raw = useStoreWatch(store ?? EMPTY_STORE, path, !store);
  useStoreVersion(store);
  const list: unknown[] = Array.isArray(raw) ? raw : [];
  const keysRef = React.useRef<number[]>([]);
  while (keysRef.current.length < list.length) keysRef.current.push(listKeySeed++);
  if (keysRef.current.length > list.length) keysRef.current = keysRef.current.slice(0, list.length);
  if (!store) return null;

  const current = () => { const value = store.getValue(path); return Array.isArray(value) ? [...value] : []; };
  const commit = (next: unknown[]) => {
    store.setValueSilently(path, next);
    store.touched.add(key);
    store.callbacks.onValuesChange?.(setIn({}, path, next), store.registeredValues());
    if (store.errors.has(key)) void store.validatePaths([path]);
  };
  const operation: FormListOperation = {
    add: (defaultValue, insertIndex) => {
      const next = current();
      const index = insertIndex === undefined || insertIndex < 0 || insertIndex > next.length ? next.length : insertIndex;
      next.splice(index, 0, defaultValue);
      keysRef.current.splice(index, 0, listKeySeed++);
      commit(next);
    },
    remove: (index) => {
      const remove = new Set(Array.isArray(index) ? index : [index]);
      keysRef.current = keysRef.current.filter((_, position) => !remove.has(position));
      commit(current().filter((_, position) => !remove.has(position)));
    },
    move: (from, to) => {
      const next = current();
      if (from < 0 || from >= next.length || to < 0 || to >= next.length || from === to) return;
      const [item] = next.splice(from, 1);
      next.splice(to, 0, item);
      const [moved] = keysRef.current.splice(from, 1);
      keysRef.current.splice(to, 0, moved);
      commit(next);
    },
  };
  const fields: FormListFieldData[] = list.map((_, index) => ({ key: keysRef.current[index], name: index, isListField: true, fieldKey: keysRef.current[index] }));
  const errors = store.errors.get(key);
  return <ListPrefixContext.Provider value={path}>{children(fields, operation, { errors: errors?.errors ?? [], warnings: errors?.warnings ?? [] })}</ListPrefixContext.Provider>;
}

// ---------------------------------------------------------------- Form.ErrorList

function ErrorList({ errors, warnings, className }: { errors?: ReactNode[]; warnings?: ReactNode[]; className?: string; help?: ReactNode }) {
  const items = [...(errors ?? []).map((message) => ({ message, kind: "error" })), ...(warnings ?? []).map((message) => ({ message, kind: "warning" }))];
  if (!items.length) return null;
  return (
    <div className={cx("ck-form-item-explain", className)} role="alert">
      {items.map((item, index) => <div key={index} className={`ck-form-item-explain-${item.kind}`}>{item.message}</div>)}
    </div>
  );
}

// ---------------------------------------------------------------- Hooks

type WatchOptions<TForm = FormInstance> = { form?: TForm; preserve?: boolean };
type GetGeneric<TForm extends FormInstance> = Awaited<ReturnType<TForm["validateFields"]>>;

/* Überladungen wie Antd (`rc-field-form`): Pfade über die Formulartypen, Ausschnitt per Funktion, sonst frei. */
function useWatch<K1 extends keyof GetGeneric<TForm>, TForm extends FormInstance, K2 extends keyof GetGeneric<TForm>[K1], K3 extends keyof GetGeneric<TForm>[K1][K2]>(dependencies: [K1, K2, K3], form?: TForm | WatchOptions<TForm>): GetGeneric<TForm>[K1][K2][K3];
function useWatch<K1 extends keyof GetGeneric<TForm>, TForm extends FormInstance, K2 extends keyof GetGeneric<TForm>[K1]>(dependencies: [K1, K2], form?: TForm | WatchOptions<TForm>): GetGeneric<TForm>[K1][K2];
function useWatch<K extends keyof GetGeneric<TForm>, TForm extends FormInstance>(dependencies: K | [K], form?: TForm | WatchOptions<TForm>): GetGeneric<TForm>[K];
function useWatch<TForm extends FormInstance>(dependencies: [], form?: TForm | WatchOptions<TForm>): GetGeneric<TForm>;
function useWatch<TForm extends FormInstance, TSelected = unknown>(selector: (values: GetGeneric<TForm>) => TSelected, form?: TForm | WatchOptions<TForm>): TSelected;
function useWatch<ValueType = Store, TSelected = unknown>(selector: (values: ValueType) => TSelected, form?: FormInstance | WatchOptions): TSelected;
function useWatch<TForm extends FormInstance>(dependencies: NamePath | undefined, form?: TForm | WatchOptions<TForm>): any;
function useWatch<ValueType = Store>(dependencies: NamePath | undefined, form?: FormInstance | WatchOptions): ValueType;
/** Beobachtet einen Wert (Pfad) oder einen Ausschnitt (Funktion) wie Antd `Form.useWatch`. */
function useWatch<Values = any>(name: NamePath | ((values: Values) => any) | undefined, formOrOptions?: FormInstance<Values> | WatchOptions<FormInstance<Values>>): any {
  const contextStore = useFormStore();
  const form = formOrOptions && "getFieldValue" in (formOrOptions as object) ? (formOrOptions as FormInstance<Values>) : (formOrOptions as WatchOptions<FormInstance<Values>> | undefined)?.form;
  const store = form ? storeOf(form as FormInstance) : contextStore;
  const selector = typeof name === "function" ? name : null;
  const path = selector ? null : toPath(name as NamePath | undefined);
  const watched = useStoreWatch(store?.methods ? store : EMPTY_STORE, path, !store?.methods);
  if (!store?.methods) return undefined;
  return selector ? selector(watched as Values) : watched;
}

function useFormInstance<Values = any>(): FormInstance<Values> {
  const store = useFormStore();
  return (store?.instance ?? EMPTY_STORE.instance) as FormInstance<Values>;
}

export const Form = Object.assign(FormBase, {
  Item: FormItem,
  List: FormList,
  ErrorList,
  useForm,
  useWatch,
  useFormInstance,
  Provider: ({ children }: { children?: ReactNode }) => <>{children}</>,
});
/* eslint-enable @typescript-eslint/no-explicit-any */
